/*  Shared  */
import { AuvikASMLogo } from 'application/Shared/Components/App/AuvikLogo';
import { SessionHelper } from 'application/Shared/Helpers';

/*  Hooks  */
import { useSession } from 'hooks/redux/Selectors/session';
import { useTenantInfoByTenantId } from 'hooks/redux/Selectors/tenants';

/*  React  */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const defaultState = {
    brandLogo: '',
    brandLogoSet: false,
    noASM: false
};

/**
 * Logo component that displays the logo for the current tenant.
 * If the tenant has a custom logo, it will display the custom logo.
 * Otherwise, it will display the Auvik ASM logo.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.checkTenantSettings - Whether to check the tenant settings for branding.
 * @param {boolean} props.lightColor - Whether to use the light color for the logo.
 * @param {React.CSSProperties} props.style - The style for the logo.
 * @param {React.CSSProperties} props.containerStyle - The style for the container.
 * @returns {React.ReactNode} The logo component.
 */

export const Logo = ({
    checkTenantSettings,
    lightColor,
    style,
    containerStyle
}: {
    checkTenantSettings: boolean;
    lightColor: boolean;
    style: React.CSSProperties;
    containerStyle: React.CSSProperties;
}) => {
    const { t } = useTranslation();

    const session = useSession();
    const sessionHelper = new SessionHelper(session);

    const { partnerId } = useParams();
    const partnerTenantInfo = useTenantInfoByTenantId(partnerId);
    const partnerTenant = partnerTenantInfo?.tenant;

    // If there's a partner tenant, then we're viewing a partner tenant page.
    // Otherwise, use the session's tenant as a fallback.
    const tenantToDisplay = partnerTenant || sessionHelper.getTenant();

    const brandState = useMemo(() => {
        if (!checkTenantSettings) {
            return defaultState;
        }

        const settings = tenantToDisplay?.settings || {};

        const { noSaaslioBrand, brandEnabled, brandLogo } = settings;

        return {
            ...defaultState,
            brandLogo: brandEnabled && brandLogo ? brandLogo : '',
            brandLogoSet: !!(brandEnabled && brandLogo),
            noASM: noSaaslioBrand || false
        };
    }, [checkTenantSettings, tenantToDisplay]);

    if (brandState.brandLogoSet) {
        return (
            <div
                data-testid="logoRenderBrand"
                style={{
                    height: 'max-content',
                    marginBottom: '24px',
                    width: '150px',
                    ...containerStyle
                }}
            >
                <img
                    src={brandState.brandLogo}
                    style={style}
                    alt={t('Logo.forCompany')}
                />
            </div>
        );
    }

    if (checkTenantSettings && brandState.noASM) {
        return null;
    }

    return (
        <AuvikASMLogo
            lightColor={lightColor}
            style={{
                height: 'max-content',
                marginBottom: '24px',
                width: '150px',
                ...style
            }}
        />
    );
};
